import React, { useCallback } from 'react';
import type { BrowseRowState } from 'fym-shared/src/music24/browseSearch';
import { TypedMemo } from '../../../common/types';
import { SMLLink } from '../../components/SMLLink/SMLLink';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { useBrowseRowContext } from '../BrowseContext';

interface BrowseOpenLinkProps {
  kind: 'artist' | 'track' | 'playlist';
  id: string;
  state: BrowseRowState;
  className?: string;
  ariaLabel?: string;
  children: React.ReactNode;
}

/**
 * Opens the details page of a row. The details call charges the credit, not this link. With no
 * credit left, a row that costs one does not open: the click shows the credits (design b5).
 */
const BrowseOpenLinkComponent: React.FC<BrowseOpenLinkProps> = ({
  kind,
  id,
  state,
  className,
  ariaLabel,
  children,
}) => {
  const { outOfCredits, onBlockedOpen, onOpenRow } = useBrowseRowContext();
  const blocked = outOfCredits && state === 'costsCredit';
  const onBlockedClick = useCallback(() => onBlockedOpen(), [onBlockedOpen]);

  if (kind === 'artist') {
    return (
      <SMLLink
        to={SMLRoutes.artistDetails}
        params={{ artistId: id }}
        className={className}
        ariaLabel={ariaLabel}
        onClick={blocked ? onBlockedClick : undefined}
        onNavigate={onOpenRow}
      >
        {children}
      </SMLLink>
    );
  }
  if (kind === 'track') {
    return (
      <SMLLink
        to={SMLRoutes.trackDetails}
        params={{ trackId: id }}
        className={className}
        ariaLabel={ariaLabel}
        onClick={blocked ? onBlockedClick : undefined}
        onNavigate={onOpenRow}
      >
        {children}
      </SMLLink>
    );
  }
  return (
    <SMLLink
      to={SMLRoutes.playlistDetails}
      params={{ playlistId: id }}
      className={className}
      ariaLabel={ariaLabel}
      onNavigate={onOpenRow}
    >
      {children}
    </SMLLink>
  );
};

export const BrowseOpenLink = TypedMemo(BrowseOpenLinkComponent);
